import React, { useState } from 'react';

// Update the component signature
const AddVariant = ({ onSubmit, isSubmitting, formId = "addVariantForm" }) => {
  const [variantName, setVariantName] = useState('');
  const [values, setValues] = useState(['']);
  const [errors, setErrors] = useState({
    name: '',
    values: []
  });

  const handleAddValue = () => {
    setValues([...values, '']);
  };

  const handleRemoveValue = (index) => {
    const newValues = [...values];
    newValues.splice(index, 1);
    setValues(newValues);

    // Also remove any error for this index
    const newErrors = { ...errors };
    if (newErrors.values) {
      newErrors.values = newErrors.values.filter((_, i) => i !== index);
      setErrors(newErrors);
    }
  };

  const handleValueChange = (index, newValue) => {
    const newValues = [...values];
    newValues[index] = newValue;
    setValues(newValues);

    // Clear error if value is not empty
    if (newValue && errors.values && errors.values[index]) {
      const newErrors = { ...errors };
      newErrors.values[index] = '';
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: '',
      values: Array(values.length).fill('')
    };

    // Validate name
    if (!variantName.trim()) {
      newErrors.name = 'Le nom de la variante est obligatoire';
      isValid = false;
    }

    // Validate values (at least one non-empty value)
    const validValues = values.filter(val => val.trim() !== '');
    if (validValues.length === 0) {
      newErrors.values[0] = 'Au moins une valeur est requise';
      isValid = false;
    }

    // Check for empty values
    values.forEach((val, index) => {
      if (!val.trim() && index < values.length) {
        newErrors.values[index] = 'La valeur ne peut pas être vide';
        isValid = false;
      }
    });

    // Check for duplicate values
    const uniqueValues = new Set();
    values.forEach((val, index) => {
      const trimmedValue = val.trim();
      if (trimmedValue && uniqueValues.has(trimmedValue)) {
        newErrors.values[index] = 'Cette valeur existe déjà';
        isValid = false;
      }
      uniqueValues.add(trimmedValue);
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Filter out empty values
    const filteredValues = values.filter(val => val.trim() !== '');
    
    onSubmit({
      name: variantName,
      values: filteredValues
    });
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6">
      {/* Variant name field with enhanced styling */}
      <div>
        <label htmlFor="variant-name" className="block text-sm font-medium text-gray-700 mb-1">
          Nom de la variante <span className="text-red-500">*</span>
        </label>
        <div className="relative rounded-md shadow-sm">
          <input
            type="text"
            id="variant-name"
            name="variant-name"
            value={variantName}
            onChange={(e) => {
              setVariantName(e.target.value);
              if (e.target.value) setErrors({...errors, name: ''});
            }}
            className={`block w-full px-3 py-2.5 border ${
              errors.name ? 'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
            } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-200 sm:text-sm`}
            placeholder="Ex: Couleur, Taille, Pointure"
          />
          {errors.name && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
        {errors.name && (
          <p className="mt-1.5 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      {/* Variant values section with enhanced styling */}
      <div className="pt-2 space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-medium text-gray-700 flex items-center">
            Valeurs <span className="text-red-500 ml-0.5">*</span>
            <span className="ml-2 text-xs text-gray-500 font-normal">(ex: Rouge, Bleu, Vert)</span>
          </h4>
          <div className="flex items-center">
            <span className="text-xs text-gray-500 mr-3">
              {values.length} {values.length > 1 ? 'valeurs' : 'valeur'}
            </span>
            <button
              type="button"
              onClick={handleAddValue}
              className="inline-flex items-center px-2.5 py-1.5 border border-[#174540] text-xs font-medium rounded text-[#174540] bg-white hover:bg-[#f0f7f6] focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#174540] transition-colors duration-200"
            >
              <svg className="-ml-0.5 mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Ajouter une valeur
            </button>
          </div>
        </div>

        {/* Scrollable container for values */}
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="max-h-[300px] overflow-y-auto">
            <div className="space-y-0 divide-y divide-gray-100">
              {values.map((value, index) => (
                <div key={index} className="flex items-center group px-3 py-2 bg-white hover:bg-gray-50">
                  <div className="flex-grow">
                    <div className="relative rounded-md">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 font-medium sm:text-sm">{index + 1}.</span>
                      </div>
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                        className={`block w-full pl-10 pr-10 py-2 border ${
                          errors.values && errors.values[index]
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                        } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-200 sm:text-sm`}
                        placeholder={`Valeur ${index + 1}`}
                      />
                      {errors.values && errors.values[index] && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    {errors.values && errors.values[index] && (
                      <p className="mt-1 text-sm text-red-600">{errors.values[index]}</p>
                    )}
                  </div>
                  {values.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveValue(index)}
                      className="ml-3 flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200"
                      title="Supprimer cette valeur"
                    >
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center pt-1 space-x-1.5 text-xs text-gray-500">
        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>Tous les champs marqués avec <span className="text-red-500">*</span> sont obligatoires</span>
      </div>
      
      {/* Submit buttons are handled by the Modal component */}
    </form>
  );
};

export default AddVariant;